import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';
import {
  JourneyGraph,
  JourneyGraphNode,
  JourneyGraphNodeAlert,
  JourneyGraphNodeAssignConversation,
  JourneyGraphNodeBranch,
  JourneyGraphNodeChatMessage,
  JourneyGraphNodeData,
  JourneyGraphNodeInboundCall,
  JourneyGraphNodeInboundChat,
  JourneyGraphNodeQuestion,
  JourneyGraphNodeSchedule,
  JourneyGraphNodeType,
  JourneyGraphNodeUpdateMetadata,
  JourneyGraphNodeUpdateStage,
  JourneyGraphNodeVideo,
  JourneyQuestionType,
} from 'types/journey';
import {
  BusinessRulesConditions,
  BusinessRulesGroup,
  BusinessRulesRule,
  BusinessRulesRuleGroup,
  BusinessRulesRuleName,
  BusinessRulesRuleType,
} from 'sb-lib/types/business-rules-types';

export enum ProblemCode {
  DEPENDENT_BRANCH_VALUE_MISSING = 'DEPENDENT_BRANCH_VALUE_MISSING',
  MORE_THAN_ONE_KNOWN_CHILDREN = 'MORE_THAN_ONE_KNOWN_CHILDREN',
  QUESTION_NODE_SKIP_WOULD_BE_INVALIDATED = 'QUESTION_NODE_SKIP_WOULD_BE_INVALIDATED',
  QUESTION_NODE_SKIP_INVALIDATED = 'QUESTION_NODE_SKIP_INVALIDATED',
}
export type Problem = null | { code: ProblemCode };
export interface GraphMutateResult {
  journeyGraph: JourneyGraph;
  problem?: Problem;
  createdChildNodeName?: string;
}

export const isRoot = (journeyGraph: JourneyGraph, nodeName: string) => {
  return journeyGraph.root === nodeName;
};

/**
 * Given a journey graph, checks that all invariants hold.
 * Optionally, fixes any broken invariants if the resolveProblems
 * argument is true.
 *
 * @param journeyGraph The full journey graph
 * @param resolveProblems If true, resolve any broken invariants
 * @returns A copy of the graph, and any detected problems
 */
export const checkGraphInvariants = (
  journeyGraph: JourneyGraph,
  resolveProblems = false,
): GraphMutateResult => {
  let g = cloneDeep(journeyGraph);
  let problem: Problem = null;
  for (const nodeName of Object.keys(g.nodes)) {
    const node = g.nodes[nodeName];
    if (isQuestionNode(node) && node.data.skipTo != null) {
      const targettableNodes = findNodesTargettableFromQuestionNode(nodeName, g);
      if (!targettableNodes.includes(node.data.skipTo)) {
        if (!resolveProblems) {
          problem = { code: ProblemCode.QUESTION_NODE_SKIP_INVALIDATED };
          return { journeyGraph: g, problem };
        }
        const fixed = { ...node, data: { ...node.data, skipTo: null } };
        g = { ...g, nodes: { ...g.nodes, [nodeName]: fixed } };
      }
    }
  }
  return { journeyGraph: g, problem };
};

export const getDescendantNodeNames = (
  graph: Record<string, object>,
  root: string,
  nodeName: string,
  nodesToRemove: string[] = [],
) => {
  if (!graph[nodeName]) {
    return nodesToRemove;
  } else {
    if (nodeName !== root && !nodesToRemove.includes(nodeName)) {
      nodesToRemove.push(nodeName);
    }
    const children = Object.keys(graph[nodeName]);
    if (children) {
      children.forEach(child => {
        nodesToRemove.push(child);
        getDescendantNodeNames(graph, root, child, nodesToRemove);
      });
    }
  }
  return nodesToRemove;
};

export const getParentName = (graph: JourneyGraph, nodeName: string) => {
  // Root?
  if (nodeName === graph.root) {
    // no parent
    return '';
  }
  // Non-root
  for (const [key, value] of Object.entries(graph.graph)) {
    if (Object.keys(value).includes(nodeName)) {
      return key;
    }
  }

  return ''; // orphan
};

/**
 * Get the child of given nodeName, if it has more than one, only the first is returned.
 * @param graph
 * @param nodeName
 * @returns Child node name, or empty string if none found
 */
export const getChildName = (graph: JourneyGraph, nodeName: string) => {
  if (!graph.graph[nodeName]) return '';
  const edges = Object.keys(graph.graph[nodeName]);
  if (edges.length) {
    return edges[0];
  }
  return ''; // childless
};

/**
 * Get the children nodes of given nodeName.
 * @param graph
 * @param nodeName
 * @returns Object of child key to child node mapping
 */
export const getChildNodes = (graph: JourneyGraph, nodeName: string) => {
  if (!graph.graph[nodeName]) return {} as JourneyGraph['nodes'];
  const edges = { ...(graph.graph[nodeName] || {}) };
  for (const [k] of Object.entries(edges)) {
    edges[k] = graph.nodes[k];
  }
  return edges as JourneyGraph['nodes'];
};

/**
 * Delete the childName edge from parentName. If replaceChildName is provided,
 * add it as an edge from the parentName node. Handles branches.Mutates the
 * provided JourneyGraph.
 * @param graph
 * @param parentName
 * @param childName
 * @param replaceChildName
 */
export const deleteChildEdge = (
  graph: JourneyGraph,
  parentName: string,
  childName: string,
  replaceChildName: string,
) => {
  const parentEdges = graph.graph[parentName];
  const parentNode = graph.nodes[parentName];
  if (parentEdges) {
    delete parentEdges[childName];
    if (replaceChildName) {
      parentEdges[replaceChildName] = {};
    }
  }
  // Update branch node boolean child refs
  if (parentNode && isBranchNode(parentNode)) {
    if (parentNode.data.trueChildName === childName) {
      parentNode.data.trueChildName = replaceChildName;
    } else if (parentNode.data.falseChildName === childName) {
      parentNode.data.falseChildName = replaceChildName;
    }
  }
  // Update question node child refs
  if (parentNode && isQuestionNode(parentNode)) {
    if (parentNode.data.childNodes) {
      const childIndex = parentNode.data.childNodes.indexOf(childName);
      if (childIndex >= 0) {
        parentNode.data.childNodes[childIndex] = replaceChildName;
      }
    }
  }
};

export const updateAlertNode = (
  nodeName: string,
  alert: JourneyGraphNodeAlert,
  g: JourneyGraph,
) => updateNode(nodeName, alert, JourneyGraphNodeType.alert, g);

export const updateChatMessageNode = (
  nodeName: string,
  chatMessage: JourneyGraphNodeChatMessage,
  g: JourneyGraph,
) => updateNode(nodeName, chatMessage, JourneyGraphNodeType.chat_message, g);

export const updateAssignConversationNode = (
  nodeName: string,
  data: JourneyGraphNodeAssignConversation,
  g: JourneyGraph,
) => updateNode(nodeName, data, JourneyGraphNodeType.assign_conversation, g);

export const updateScheduleNode = (
  nodeName: string,
  schedule: JourneyGraphNodeSchedule,
  g: JourneyGraph,
) => updateNode(nodeName, schedule, JourneyGraphNodeType.schedule, g);

export const updateCloseConversationNode = (nodeName: string, g: JourneyGraph) =>
  updateNode(nodeName, {}, JourneyGraphNodeType.close_conversation, g);

export const updateClearbitRevealNode = (nodeName: string, g: JourneyGraph) =>
  updateNode(nodeName, {}, JourneyGraphNodeType.clearbit_reveal, g);

export const updateSixsenseCompanyDetailNode = (nodeName: string, g: JourneyGraph) =>
  updateNode(nodeName, {}, JourneyGraphNodeType.sixsense_company_details, g);

export const updateUpdateStageNode = (
  nodeName: string,
  stageUpdate: JourneyGraphNodeUpdateStage,
  g: JourneyGraph,
) => updateNode(nodeName, stageUpdate, JourneyGraphNodeType.update_stage, g);

export const updateUpdateMetadataNode = (
  nodeName: string,
  metadataUpdate: JourneyGraphNodeUpdateMetadata,
  g: JourneyGraph,
) => updateNode(nodeName, metadataUpdate, JourneyGraphNodeType.update_metadata, g);

export const updateVideoNode = (
  nodeName: string,
  videoNode: JourneyGraphNodeVideo,
  g: JourneyGraph,
) => updateNode(nodeName, videoNode, JourneyGraphNodeType.video, g);

export const updateInboundCallNode = (
  nodeName: string,
  inboundCall: JourneyGraphNodeInboundCall,
  g: JourneyGraph,
) => {
  const type = JourneyGraphNodeType.inbound_call;
  return updateInboundNode(nodeName, inboundCall, type, g);
};

export const updateInboundChatNode = (
  nodeName: string,
  inboundChat: JourneyGraphNodeInboundChat,
  g: JourneyGraph,
) => {
  const type = JourneyGraphNodeType.inbound_chat;
  return updateInboundNode(nodeName, inboundChat, type, g);
};

export const updateInboundNode = (
  nodeName: string,
  inboundBase: JourneyGraphNodeInboundChat | JourneyGraphNodeInboundCall,
  type: JourneyGraphNodeType.inbound_call | JourneyGraphNodeType.inbound_chat,
  g: JourneyGraph,
) => {
  const graphCopy = cloneDeep(g);
  const nodeData = cloneDeep(inboundBase);

  // Ensure it has a parent
  const parentName = getParentName(g, nodeName);
  if (!parentName && !isRoot(g, nodeName)) {
    throw new Error(`Cannot update (name=${nodeName}), it has no parent`);
  }

  // Are we converting it?
  const existingChildNode = g.graph[nodeName] && Object.keys(g.graph[nodeName])[0];

  // Update or create child nodes
  const trueChildName = nodeData.trueChildName || v4();
  const falseChildName = nodeData.falseChildName || existingChildNode || v4();
  const trueChildNode = g.nodes[trueChildName] || makeUnknownNode();
  const falseChildNode = g.nodes[falseChildName] || makeUnknownNode();

  // Assign node + children
  nodeData.trueChildName = trueChildName;
  nodeData.falseChildName = falseChildName;
  graphCopy.graph[nodeName] = { [trueChildName]: {}, [falseChildName]: {} };
  graphCopy.nodes[nodeName] = {
    type,
    data: nodeData,
  };
  graphCopy.nodes[trueChildName] = trueChildNode;
  graphCopy.nodes[falseChildName] = falseChildNode;

  return graphCopy;
};

const updateNode = (
  nodeName: string,
  data: JourneyGraphNodeData,
  type: JourneyGraphNodeType,
  g: JourneyGraph,
) => {
  const graphCopy = cloneDeep(g);

  // Ensure it has a parent
  const parentName = getParentName(g, nodeName);
  if (!parentName && !isRoot(g, nodeName)) {
    throw new Error(`Cannot update (name=${nodeName}), it has no parent`);
  }

  const hasChildNode = g.graph[nodeName] && Object.keys(g.graph[nodeName])[0];
  const childName = hasChildNode ? Object.keys(g.graph[nodeName])[0] : v4();
  const childNode = hasChildNode ? g.nodes[childName] : makeUnknownNode();

  graphCopy.graph[nodeName] = { [childName]: {} };
  graphCopy.nodes[nodeName] = { type, data };
  graphCopy.nodes[childName] = childNode;

  return graphCopy;
};

/**
 * Insert an unknown node between two existing nodes.
 * @param parentNodeName
 * @param childNodeName
 * @param g JourneyGraph
 * @returns updated JourneyGraph and the name of the new node
 */
export const insertUnknownNode = (
  parentNodeName: string | null,
  childNodeName: string,
  g: JourneyGraph,
) => {
  const graphCopy = cloneDeep(g);
  const isRootInsert = isRoot(graphCopy, childNodeName);

  const parentNode =
    !isRootInsert && parentNodeName ? graphCopy.nodes[parentNodeName] : null;
  const childNode = graphCopy.nodes[childNodeName];
  if ((!parentNode && !isRootInsert) || !childNode) {
    throw new Error(
      `Inserting a node requires a parent and child node, but one of them was missing`,
    );
  }

  if (isUnknownNode(childNode)) {
    throw new Error(`Cannot insert an unknown node as parent of an unknown node`);
  }

  const parentEdges = parentNodeName ? graphCopy.graph[parentNodeName] : {};
  if (
    !isRootInsert &&
    (!parentEdges || !Object.keys(parentEdges).includes(childNodeName))
  ) {
    throw new Error(
      `Cannot insert child of (node=${parentNodeName}) because its edges do not contain the child node name`,
    );
  }

  const insertingUnknownNodeName = v4();
  const unknownNode = makeUnknownNode();

  // Adjust edges
  const graph = {
    ...graphCopy.graph,
    [insertingUnknownNodeName]: { [childNodeName]: {} },
  };
  if (parentNodeName) {
    // delete the old parent-child edge
    delete parentEdges[childNodeName];
    // replace with new edge
    graph[parentNodeName] = { ...parentEdges, [insertingUnknownNodeName]: {} };
  } else if (isRootInsert) {
    // set the root to this new unknown node
    graphCopy.root = insertingUnknownNodeName;
  }

  // If parent is branch, adjust boolean child ref
  if (parentNode && isBranchNode(parentNode)) {
    if (parentNode.data.trueChildName === childNodeName) {
      parentNode.data.trueChildName = insertingUnknownNodeName;
    } else {
      parentNode.data.falseChildName = insertingUnknownNodeName;
    }
  }

  // If parent is question, adjust child ref
  if (parentNode && isQuestionNode(parentNode)) {
    if (parentNode.data.childNodes) {
      const childIndex = parentNode.data.childNodes.indexOf(childNodeName);
      if (childIndex >= 0) {
        parentNode.data.childNodes[childIndex] = insertingUnknownNodeName;
      }
    }
  }

  // Adjust nodes
  const nodes = {
    ...graphCopy.nodes,
    [insertingUnknownNodeName]: unknownNode,
  };

  return {
    journeyGraph: { ...graphCopy, graph, nodes },
    newNodeName: insertingUnknownNodeName,
  };
};

export const createBranchNode = (nodeName: string, g: JourneyGraph) => {
  const graphCopy = cloneDeep(g);
  const newBranch = makeBranch();

  const childName = getChildName(g, nodeName);
  if (childName) {
    // remove the new unknown true child, and replace with existing child
    delete newBranch.graph[newBranch.node.data.trueChildName];
    delete newBranch.childNodes[newBranch.node.data.trueChildName];
    newBranch.graph[childName] = {};
    newBranch.node.data.trueChildName = childName;
  }

  graphCopy.graph[nodeName] = newBranch.graph;
  graphCopy.nodes[nodeName] = newBranch.node;
  graphCopy.nodes = {
    ...graphCopy.nodes,
    ...newBranch.childNodes, // add the true/false child nodes
  };

  return graphCopy;
};

/**
 * Delete nodeName from journey if it is of unknown type, and has a child. Otherwise
 * leave it alone. This is used to delete temporary unknown nodes during insertion if
 * it is canceled.
 * @param nodeName
 * @param g
 * @returns JourneyGraph
 */
export const deleteUnknownNonLeafNode = (nodeName: string, g: JourneyGraph) => {
  const node = g.nodes[nodeName];
  if (node && isUnknownNode(node) && getChildName(g, nodeName)) {
    const deleteResult = deleteNode(nodeName, g);
    if (deleteResult.problem) {
      throw new Error(
        `Could not delete unknown non-leaf node because: ${deleteResult.problem}`,
      );
    }
    return deleteResult.journeyGraph;
  }
  return g;
};

/**
 * Delete a node from the Journey Graph, preserving child-nodes if possible.
 * @param nodeName String
 * @param g JourneyGraph
 * @returns JourneyGraph and problem string (if problem encountered)
 */
export const deleteNode = (
  nodeName: string,
  g: JourneyGraph,
  resolveProblems = false,
): GraphMutateResult => {
  let graphCopy = cloneDeep(g);
  const nodeToDelete = graphCopy.nodes[nodeName];
  let problem: Problem = null;

  // Dependencies
  const dependents = findDependentNodes(nodeName, g);
  if (dependents.length) {
    if (!resolveProblems) {
      problem = { code: ProblemCode.DEPENDENT_BRANCH_VALUE_MISSING };
      return { journeyGraph: g, problem };
    } else {
      dependents.forEach(depNodeName => {
        const result = clearQuestionChoiceBranchNodeValue(depNodeName, nodeName, g);
        graphCopy = result.journeyGraph;
      });
    }
  }

  // Dependent question nodes
  for (const name of Object.keys(g.nodes)) {
    const node = g.nodes[name];
    if (isQuestionNode(node) && node.data.skipTo === nodeName) {
      if (!resolveProblems) {
        problem = { code: ProblemCode.QUESTION_NODE_SKIP_WOULD_BE_INVALIDATED };
        return { journeyGraph: g, problem };
      } else {
        const fixed = { ...node, data: { ...node.data, skipTo: null } };
        graphCopy = { ...graphCopy, nodes: { ...graphCopy.nodes, [name]: fixed } };
      }
    }
  }

  const parentName = getParentName(graphCopy, nodeName);
  const childName = getChildName(graphCopy, nodeName);
  const isRoot = !parentName && graphCopy.root === nodeName;

  if (
    (nodeToDelete && isBranchNode(nodeToDelete)) ||
    (isQuestionNode(nodeToDelete) &&
      nodeToDelete.data.type === JourneyQuestionType.choice)
  ) {
    // BRANCH

    // Delete children of this node
    const children = getChildNodes(graphCopy, nodeName);
    const knownChildren = Object.entries(children).filter(([_, v]) => !isUnknownNode(v));
    const unknownChildren = Object.entries(children).filter(([_, v]) => isUnknownNode(v));
    const nodesToRemove = getDescendantNodeNames(graphCopy.graph, g.root, nodeName);
    if (knownChildren.length === 1) {
      // We can just hoist the known child up & delete the unknown
      unknownChildren
        .map(([k, _]) => k)
        .forEach(n => {
          delete graphCopy.nodes[n];
          delete graphCopy.graph[n];
        });
      const isRoot = !parentName && graphCopy.root === nodeName;
      if (isRoot) {
        graphCopy.root = knownChildren[0][0];
      } else {
        deleteChildEdge(graphCopy, parentName, nodeName, knownChildren[0][0]);
      }
      delete graphCopy.graph[nodeName]; // delete edge definition
      delete graphCopy.nodes[nodeName]; // delete node definition
    } else if (knownChildren.length > 1) {
      if (resolveProblems) {
        // Delete them
        nodesToRemove.forEach(n => {
          delete graphCopy.nodes[n];
          delete graphCopy.graph[n];
        });

        // Set node to unknown type & remove edge definition
        delete graphCopy.graph[nodeName];
        graphCopy.nodes[nodeName] = {
          ...graphCopy.nodes[nodeName],
          ...makeUnknownNode(),
        };
      } else {
        // Return a problem, the so it can be decided if we want to delete the branches
        problem = { code: ProblemCode.MORE_THAN_ONE_KNOWN_CHILDREN };
        return { journeyGraph: g, problem };
      }
    } else {
      // Children are all unknown
      nodesToRemove.forEach(n => {
        delete graphCopy.nodes[n];
        delete graphCopy.graph[n];
      });

      // Set node to unknown type
      graphCopy.nodes[nodeName] = {
        ...graphCopy.nodes[nodeName],
        ...makeUnknownNode(),
      };
      // Clear edges
      delete graphCopy.graph[nodeName];
    }
  } else {
    // NON-BRANCH

    // Non-root is being deleted
    if (childName) {
      // Has child - hoist child & delete the node
      if (isRoot) {
        graphCopy.root = childName;
      } else {
        deleteChildEdge(graphCopy, parentName, nodeName, childName);
      }
      delete graphCopy.graph[nodeName]; // delete edge definition
      delete graphCopy.nodes[nodeName]; // delete node definition
    } else {
      // No child - convert this node to unknown type and leave it as child
      graphCopy.nodes[nodeName] = {
        ...graphCopy.nodes[nodeName],
        ...makeUnknownNode(),
      };
    }
  }

  return { journeyGraph: graphCopy, problem };
};

export const saveQuestionNode = (
  questionNodeName: string,
  question: JourneyGraphNodeQuestion<JourneyQuestionType>,
  g: JourneyGraph,
  resolveProblems = false,
): GraphMutateResult => {
  const graphCopy = cloneDeep(g);
  const oldNode = cloneDeep(g.nodes[questionNodeName]);

  let node = graphCopy.nodes[questionNodeName];
  let problem: null | { code: 'DEPENDENT_BRANCH_VALUE_MISSING' } = null;
  let createdChildNodeName = '';

  if (!node) {
    // TODO - ensure parent?
    node = makeUnknownNode();
    graphCopy.nodes[questionNodeName] = node;
  }

  const nodeData = isQuestionNode(node) && node.data;
  const isChoiceType = !!nodeData && isQuestionNodeDataChoice(nodeData);

  // CHOICE TYPE
  if (isChoiceType && isQuestionNodeDataChoice(question)) {
    const dependents = findDependentNodes(questionNodeName, graphCopy);
    if (dependents.length) {
      // Find out if dependents need updating
      dependents.forEach(depName => {
        const depNode = graphCopy.nodes[depName];
        if (isBranchNode(depNode)) {
          const rule = findQuestionChoiceRule(questionNodeName, depNode.data.conditions);
          rule.forEach(r => {
            // value typing allows string arrays, but we expect a string for this type
            if (r.value && !Array.isArray(r.value)) {
              const effectiveChoiceValues = question.config.options.map(
                o => o.value || o.label,
              );
              const ruleParts = r.value.split(':');
              const choicePart = ruleParts.length === 2 ? ruleParts[1] : '';
              const isChoiceOptStillValid = effectiveChoiceValues.includes(choicePart);
              if (!isChoiceOptStillValid) {
                if (resolveProblems) {
                  // Clear any branch option values that are now missing (mutate)
                  r.value = ''; // clear out the value
                } else {
                  problem = { code: 'DEPENDENT_BRANCH_VALUE_MISSING' };
                }
              }
            }
          });
        }
      });

      if (problem) {
        // Return unaltered graph with problem deets
        return { journeyGraph: g, problem, createdChildNodeName };
      }
    }
  }

  // Update child nodes
  if (!getChildName(g, questionNodeName)) {
    if (isQuestionNodeDataChoice(question)) {
      graphCopy.graph[questionNodeName] = {};
      question.childNodes = [];
      question.config.options.forEach(() => {
        createdChildNodeName = v4();
        question.childNodes?.push(createdChildNodeName);
        graphCopy.graph[questionNodeName][createdChildNodeName] = {}; // edge
        graphCopy.nodes[createdChildNodeName] = makeUnknownNode(); // node
      });
    } else {
      createdChildNodeName = v4();
      graphCopy.graph[questionNodeName] = { [createdChildNodeName]: {} }; // edge
      graphCopy.nodes[createdChildNodeName] = makeUnknownNode(); // node
    }
  } else {
    // Updates new type of question node with child nodes
    if (
      isQuestionNode(oldNode) &&
      isQuestionNodeDataChoice(oldNode.data) &&
      oldNode.data.childNodes
    ) {
      const oldOptions = oldNode.data.config.options;
      const oldChildNodes = oldNode.data.childNodes || [];
      const reused: string[] = [];

      // If the new node is also a question choice, we need to update the child nodes
      if (isQuestionNodeDataChoice(question)) {
        graphCopy.graph[questionNodeName] = {};
        question.childNodes = [];
        question.config.options.forEach(({ label }) => {
          const oldOption = oldOptions.find(o => o.label === label);
          if (oldOption) {
            // Reuse the old child node
            reused.push(oldOption.label);
            const oldChildNodeName = oldChildNodes[oldOptions.indexOf(oldOption)];
            question.childNodes?.push(oldChildNodeName);
            graphCopy.graph[questionNodeName][oldChildNodeName] = {}; // edge
          } else {
            createdChildNodeName = v4();
            question.childNodes?.push(createdChildNodeName);
            graphCopy.graph[questionNodeName][createdChildNodeName] = {}; // edge
            graphCopy.nodes[createdChildNodeName] = makeUnknownNode(); // node
          }
        });
      }
      // Remove old child nodes that are no longer needed
      oldOptions
        .filter(({ label }) => !reused.includes(label))
        .forEach(o => {
          const oldChildNodeName = oldChildNodes[oldOptions.indexOf(o)];
          delete graphCopy.nodes[oldChildNodeName];
          delete graphCopy.graph[oldChildNodeName];
        });
    } else if (isQuestionNodeDataChoice(question)) {
      // If the old node was not a question choice, but the new one is, we need to add child nodes
      question.childNodes = [getChildName(g, questionNodeName)];
      question.config.options.slice(1).forEach(() => {
        createdChildNodeName = v4();
        question.childNodes?.push(createdChildNodeName);
        graphCopy.graph[questionNodeName][createdChildNodeName] = {}; // edge
        graphCopy.nodes[createdChildNodeName] = makeUnknownNode(); // node
      });
    }
  }

  // Update the node (mutate)
  node.type = JourneyGraphNodeType.question; // convert unknowns
  node.data = cloneDeep(question);
  delete node['needsAttention'];

  return { journeyGraph: graphCopy, createdChildNodeName };
};

/**
 * Given a journey graph and the name of a question node, find all the
 * nodes that question node can safely skip to.
 *
 * @param nodeName The name of the node to look at
 * @param journeyGraph The full journey graph
 * @returns An array of the nodes that the question node can skip to
 */
export const findNodesTargettableFromQuestionNode = (
  nodeName: string,
  journeyGraph: JourneyGraph,
): string[] => {
  const descendantNodes = getDescendantNodeNames(
    journeyGraph.graph,
    journeyGraph.root,
    nodeName,
  ).filter(descendantName => {
    return (
      descendantName !== nodeName && !isUnknownNode(journeyGraph.nodes[descendantName])
    );
  });

  // Invariant: no cycles, so a node can only ever depend on the nodes
  // before it.
  const seen: Record<string, boolean> = {};
  return descendantNodes.filter(descendantName => {
    seen[descendantName] = true;
    for (const dependencyName of findDependencyNodes(descendantName, journeyGraph)) {
      if (seen[dependencyName]) {
        return false;
      }
    }
    return true;
  });
};

/**
 * Given a journey graph and a node name, find all the nodes that the
 * given nodes depends on.
 *
 * @param nodeName The name of the node to search for dependencies of
 * @param journeyGraph The full journey graph
 * @returns An array of the dependency node names
 */
export const findDependencyNodes = (
  nodeName: string,
  journeyGraph: JourneyGraph,
): string[] => {
  const node = journeyGraph.nodes[nodeName];
  const nodes: string[] = [];

  if (isBranchNode(node)) {
    Object.values(BusinessRulesRuleType).forEach(brrt => {
      if (node.data.conditions && brrt in node.data.conditions) {
        node.data.conditions[brrt].forEach(rg => {
          findRulesInGroup(
            rg,
            r => r.name === BusinessRulesRuleName.question_choice,
          ).forEach(rule => {
            if (typeof rule.value === 'string') {
              nodes.push(rule.value.split(':')[0]);
            }
          });
        });
      }
    });
  }

  return removeDuplicates(nodes);
};

/**
 * Given a journey graph and node name, find any node names that depend upon
 * the node name provided.
 *
 * @param nodeName The name of the node to search for dependents of
 * @param journeyGraph The full journey graph
 * @returns An array of the dependent node's names, or empty if none are found.
 */
export const findDependentNodes = (
  nodeName: string,
  journeyGraph: JourneyGraph,
): string[] => {
  const node = journeyGraph.nodes[nodeName];
  let deps: string[] = [];

  // Check for question deps
  if (isQuestionNode(node)) {
    // Look for branch nodes with question_choice conditions
    if (node.data.type === JourneyQuestionType.choice) {
      deps = [...findQuestionChoiceBranches(nodeName, journeyGraph)];
    }

    // TODO - other question types
  }

  return removeDuplicates(deps);
};

const removeDuplicates = (xs: string[]): string[] => {
  const seen: Record<string, boolean> = {};
  return xs.filter(x => {
    if (seen[x]) return false;
    seen[x] = true;
    return true;
  });
};

/**
 * Given a journey and question choice node name, find any
 * branch nodes that reference that question choice node.
 *
 * @param questionChoiceNodeName
 * @param g
 * @returns An array of branch nodes that reference the provided node name
 */
export const findQuestionChoiceBranches = (
  questionChoiceNodeName: string,
  g: JourneyGraph,
): string[] => {
  const branchNodeNames: string[] = [];
  // Search all the node defs
  for (const [nodeName, node] of Object.entries(g.nodes)) {
    if (isBranchNode(node)) {
      const found = findQuestionChoiceRule(questionChoiceNodeName, node.data.conditions);
      if (found.length) {
        branchNodeNames.push(nodeName);
      }
    }
  }
  return branchNodeNames;
};

/**
 * Given a question choice node name and a set of business rules conditions,
 * find any business rules rule that references said node name.
 *
 * @param questionChoiceNodeName The name of the question choice node
 * @param conditions The branch node conditions to search
 * @returns Array of rules referencing the name, or empty if none found
 */
export const findQuestionChoiceRule = (
  questionChoiceNodeName: string,
  conditions?: BusinessRulesConditions,
) => {
  const rules: BusinessRulesRule[] = [];
  if (!conditions) {
    return rules;
  }
  Object.values(BusinessRulesRuleType).forEach(brrt => {
    if (brrt in conditions) {
      conditions[brrt].forEach(rg => {
        findRulesInGroup(rg, r =>
          isRuleForQuestionChoice(questionChoiceNodeName, r),
        ).forEach(rule => rules.push(rule));
      });
    }
  });

  return rules;
};

const findRulesInGroup = (
  rrg: BusinessRulesGroup,
  predicate: (rule: BusinessRulesRule) => boolean,
): BusinessRulesRule[] => {
  const group = isBusinesRulesGroupAGroup(rrg);
  const rule = isBusinessRulesGroupARule(rrg);

  const rules: BusinessRulesRule[] = [];
  if (group) {
    Object.values(BusinessRulesRuleType).forEach(brrt => {
      (group[brrt] || []).forEach(rule => {
        if (predicate(rule)) {
          rules.push(rule);
        }
      });
    });
  } else if (rule && predicate(rule)) {
    rules.push(rule);
  }
  return rules;
};

/**
 * Given a business rule and question choice node name, return
 * if the rule references the name (does it depend on the named node?)
 *
 * @param questionChoiceNodeName Name to check for in the rule
 * @param rule Business rule to check
 * @returns boolean expressing if the rule references the named node or not
 */
const isRuleForQuestionChoice = (
  questionChoiceNodeName: string,
  rule: BusinessRulesRule,
) => {
  const isChoice = rule.name === BusinessRulesRuleName.question_choice;
  const isDepValue =
    typeof rule.value === 'string' && rule.value.startsWith(`${questionChoiceNodeName}:`);
  return isChoice && isDepValue;
};

/**
 * Given a BusinessRulesGroup, determine if it is a group (not a single rule).
 * @param rg A business rules group
 * @returns boolean expressing if it is a group or not
 */
const isBusinesRulesGroupAGroup = (rg: BusinessRulesGroup) => {
  if ('all' in rg || 'any' in rg) {
    return rg as BusinessRulesRuleGroup;
  }
  return false;
};

/**
 * Given a BusinessRulesGroup, determine if it is a single rule (not a group)
 * @param rg A business rules group
 * @returns boolean expressing if it is a single rule or not
 */
const isBusinessRulesGroupARule = (rg: BusinessRulesGroup) => {
  if ('name' in rg && 'operator' in rg && 'value' in rg) {
    return rg as BusinessRulesRule;
  }
  return false;
};

/**
 * Clear the branch node options for named question branch.
 * @param branchNodeName
 * @param journeyGraph
 * @returns Count of values modified
 */
export const clearQuestionChoiceBranchNodeValue = (
  branchNodeName: string,
  questionChoiceNodeName: string,
  journeyGraph: JourneyGraph,
) => {
  let modifiedCount = 0;

  const graphCopy = cloneDeep(journeyGraph);
  const question = graphCopy.nodes[questionChoiceNodeName];
  const branch = graphCopy.nodes[branchNodeName];

  if (!question) {
    throw new Error(`Could not find question node (name=${questionChoiceNodeName})`);
  }

  if (!branch) {
    throw new Error(`Could not find branch node (name=${branchNodeName})`);
  }

  if (branch.type !== JourneyGraphNodeType.branch) {
    throw new Error(`Named node is not a branch (name=${branchNodeName})`);
  }

  const branchData = branch.data as JourneyGraphNodeBranch;
  const rules = findQuestionChoiceRule(questionChoiceNodeName, branchData.conditions);

  rules.forEach(r => {
    r.value = ''; // clear out the value
    modifiedCount++;
  });

  return { journeyGraph: graphCopy, modifiedCount };
};

export interface GraphScrubResult {
  journeyGraph: JourneyGraph;
  scrubbedNodes: string[];
}

/**
 * Given a template's journey graph, scrub any potentially sensitive
 * information from it.
 *
 * @param journeyGraph The full journey graph
 * @returns A copy of the graph with sensitive data scrubbed plus a
 * list of the nodes that were scrubbed.
 */
export const scrubTemplateGraph = (journeyGraph: JourneyGraph): GraphScrubResult => {
  const result = cloneDeep(journeyGraph);
  const scrubbed: string[] = [];
  for (const name of Object.keys(result.nodes)) {
    const node = result.nodes[name];
    if (!node) {
      continue;
    } else if (isAlertNode(node)) {
      scrubbed.push(name);
      result.nodes[name] = {
        ...node,
        data: {
          ...node.data,
          userIds: [],
          userGroupIds: [],
        },
        needsAttention: true,
      };
    } else if (isQuestionNode(node)) {
      // In the case of question nodes with custom keys, don't scrub
      // any data, but set its needsAttention flag so that, if the
      // account that's applying a template doesn't already have a
      // custom metadata field by that name, it can be created.
      if (node.data.config.key) {
        scrubbed.push(name);
        result.nodes[name] = {
          ...node,
          needsAttention: true,
        };
      }
    } else if (isScheduleNode(node)) {
      scrubbed.push(name);
      result.nodes[name] = {
        ...node,
        data: {
          ...node.data,
          calendarEventTypeId: 0,
        },
        needsAttention: true,
      };
    } else if (isVideoNode(node)) {
      scrubbed.push(name);
      result.nodes[name] = {
        ...node,
        data: {
          ...node.data,
          journeyVideoId: '',
        },
        needsAttention: true,
      };
    }
  }
  return {
    journeyGraph: result,
    scrubbedNodes: scrubbed,
  };
};

// TS type narrowing using predicates
// https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates

export const isAlertNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.alert> => {
  return node.type === JourneyGraphNodeType.alert;
};

export const isExpandWidgetNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.expand_widget> => {
  return node.type === JourneyGraphNodeType.expand_widget;
};

export const isBranchNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.branch> => {
  const { branch, inbound_call, inbound_chat } = JourneyGraphNodeType;
  return [branch, inbound_call, inbound_chat].includes(node.type);
};

export const isInboundChatNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.inbound_chat> => {
  return node.type === JourneyGraphNodeType.inbound_chat;
};

export const isInboundCallNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.inbound_call> => {
  return node.type === JourneyGraphNodeType.inbound_call;
};

export const isClearbitRevealNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.clearbit_reveal> => {
  return node.type === JourneyGraphNodeType.clearbit_reveal;
};

export const isQuestionNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.question> => {
  return node && 'type' in node && node.type === JourneyGraphNodeType.question;
};

export const isQuestionNodeDataChoice = (
  nodeData: JourneyGraphNodeQuestion<JourneyQuestionType>,
): nodeData is JourneyGraphNodeQuestion<JourneyQuestionType.choice> => {
  return nodeData.type === JourneyQuestionType.choice;
};

export const isChatMessageNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.chat_message> => {
  return node && 'type' in node && node.type === JourneyGraphNodeType.chat_message;
};

export const isScheduleNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.schedule> => {
  return node && 'type' in node && node.type === JourneyGraphNodeType.schedule;
};

export const isVideoNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.video> => {
  return node && 'type' in node && node.type === JourneyGraphNodeType.video;
};

export const isUnknownNode = (
  node: JourneyGraphNode<JourneyGraphNodeType>,
): node is JourneyGraphNode<JourneyGraphNodeType.unknown> => {
  if (!node) return false;
  return node.type === JourneyGraphNodeType.unknown;
};

export const makeUnknownNode = (): JourneyGraphNode<JourneyGraphNodeType.unknown> => {
  return {
    type: JourneyGraphNodeType.unknown,
    data: {},
  };
};

/**
 * Make a default branch node, graph (edges) & child nodes. Branch nodes evaluate to
 * true or false with exaclty one child for each.
 *
 * @returns A branch node along with its children & edges
 */
export const makeBranch = (): {
  node: JourneyGraphNode<JourneyGraphNodeType.branch>;
  graph: Record<string, {}>;
  childNodes: Record<string, JourneyGraphNode<JourneyGraphNodeType.unknown>>;
} => {
  const trueChildName = v4();
  const falseChildName = v4();

  const childNodes = {
    [trueChildName]: makeUnknownNode(),
    [falseChildName]: makeUnknownNode(),
  };

  const conditions = ({
    all: [],
  } as unknown) as BusinessRulesConditions;

  const node = {
    type: JourneyGraphNodeType.branch,
    data: { conditions, trueChildName, falseChildName },
  } as JourneyGraphNode<JourneyGraphNodeType.branch>;

  const graph = {
    [trueChildName]: {},
    [falseChildName]: {},
  };

  return { node, graph, childNodes };
};
